import React, { useEffect, useRef, useState } from 'react';
import {
  CheckCircle,
  ChevronRight,
  LayoutGrid,
  LogOut,
  MessageCircle,
  PlusCircle,
  Shield,
  Sparkles,
  Star,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import ReviewModal from '../components/ReviewModal';
import { trackLogin, trackLogout, flushAnalytics } from '../utils/siteAnalytics';

const API = 'https://e4u-backend.onrender.com';
const GOOGLE_CLIENT_ID = '281405583072-n7rkibd2qc0afs76dve0kgbsi9p15jfk.apps.googleusercontent.com';

// function GoogleIcon() {
//   return (
//     <svg width="24" height="24" className="google-icon" viewBox="0 0 24 24">
//       <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
//       <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
//       <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
//       <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
//     </svg>
//   );
// }

function LoginWall({ onGoogleSignIn }) {
  const googleBtnRef = useRef(null);

  useEffect(() => {
    // Load Google Identity Services script
    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: onGoogleSignIn,
        });
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          width: '100%',
        });
      };
      document.body.appendChild(script);
    } else {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: onGoogleSignIn,
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline',
        size: 'large',
        width: '100%',
      });
    }
  }, [onGoogleSignIn]);

  return (
    <div className="login-wall">
      <div className="login-title">Join Dealr</div>
      <div className="login-sub">Sign in to post ads, chat with sellers, manage your listings, and more.</div>
      <div  ref={googleBtnRef} style={{ margin: '16px 0' }} />
      <div className="feature-list">
        {[
          { Icon: LayoutGrid, text: 'Post & manage your ads easily' },
          { Icon: MessageCircle, text: 'Chat directly with buyers & sellers' },
          { Icon: Sparkles, text: 'Get AI-powered insights on your listings' },
        ].map(({ Icon, text }) => (
          <div key={text} className="feature-row">
            <div className="feature-icon">
              <Icon size={18} color="var(--primary)" strokeWidth={2} />
            </div>
            <div className="feature-text">{text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function ProfilePage() {
  const { user, login, logout, navigate, showToast, showModal, setHasConsented, apiFetch } = useApp();
  const [pendingReviews, setPendingReviews] = useState([]);
  const [activeReview, setActiveReview] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    async function loadPending() {
      try {
        const data = await apiFetch('/api/reviews/pending');
        if (!cancelled) setPendingReviews(data.pending || []);
      } catch { /* ignore */ }
    }
    loadPending();
    return () => { cancelled = true; };
  }, [user, apiFetch]);

  // Google Sign-In callback
  const handleGoogleSignIn = async (response) => {
    try {
          const token = response.credential;
      // Send the credential (ID token) to your backend for verification and login
      const res = await fetch(`${API}/api/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) throw new Error('Google sign-in failed');
      const data = await res.json();
      const {user: usr } = data;
      if (token && usr) {
        console.log('Google sign-in successful:', data.token);
                localStorage.setItem("authToken", data.token);
        const userHasConsented = !!(usr.hasConsented || usr.isConsented || usr.consentAccepted);
        const normalizedUser = { ...usr, hasConsented: userHasConsented };
        login(token, normalizedUser);
        trackLogin();
        showToast('Signed in successfully!', 'success');
        if (!userHasConsented) navigate('consent');
      } else {
        showToast('Google sign-in failed.', 'error');
      }
    } catch (err) {
      showToast('Google sign-in failed.', 'error');
    }
  };

  const handleLogout = () => {
    showModal(
      'Confirm Logout',
      'Are you sure you want to logout?',
      <LogOut size={22} color="var(--error)" strokeWidth={2} />,
      () => {
      trackLogout();
      flushAnalytics();
      logout();
      navigate('home');
      showToast('Logged out.', 'success');
    }
    );
  };

  if (!user) return <LoginWall onGoogleSignIn={handleGoogleSignIn} />;

  const actions = [
    { Icon: LayoutGrid, bg: '#eff6ff', color: 'var(--primary)', label: 'My Ads', page: 'my-ads' },
    { Icon: MessageCircle, bg: '#f0fdf4', color: 'var(--success)', label: 'Messages', page: 'messages' },
    { Icon: PlusCircle, bg: '#fff7ed', color: '#f97316', label: 'Post a New Ad', page: 'post' },
    { Icon: Shield, bg: '#f5f3ff', color: '#8b5cf6', label: 'Privacy & Terms', page: 'consent' },
  ];

  return (
    <div style={{ maxWidth: 480 }}>
      <div className="user-card">
        {user.profilePic
          ? <img className="user-avatar" src={user.profilePic} alt={user.name} />
          : <div className="user-avatar-fallback">{user.name?.charAt(0) || 'U'}</div>}
        <div>
          <div className="user-name">{user.name}</div>
          <div className="user-email">{user.email}</div>
          <div className="verified-badge">
            <CheckCircle size={12} color="var(--success)" /> Verified with Google
          </div>
        </div>
      </div>

      {pendingReviews.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Pending Reviews
          </div>
          <div className="actions-card" style={{ marginBottom: 20 }}>
            {pendingReviews.map((item) => (
              <div key={item.adId} className="action-row" onClick={() => setActiveReview(item)}>
                <div className="action-icon" style={{ background: '#fffbeb' }}>
                  <Star size={16} color="#f59e0b" fill="#f59e0b" />
                </div>
                <div style={{ flex: 1 }}>
                  <div className="action-text">{item.adTitle}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>Rate {item.revieweeName}</div>
                </div>
                <ChevronRight size={16} color="var(--border)" strokeWidth={2} />
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Quick Actions
      </div>

      <div className="actions-card">
        {actions.map(({ Icon, bg, color, label, page }) => (
          <div key={page} className="action-row" onClick={() => navigate(page)}>
            <div className="action-icon" style={{ background: bg }}>
              <Icon size={18} color={color} strokeWidth={2} />
            </div>
            <div className="action-text">{label}</div>
            <ChevronRight size={16} color="var(--border)" strokeWidth={2} />
          </div>
        ))}
      </div>

      <button className="logout-btn" onClick={handleLogout}>
        <LogOut size={18} color="var(--error)" /> Logout
      </button>
      <div className="version-text">Dealr v1.0.0</div>

      {activeReview && (
        <ReviewModal
          adId={activeReview.adId}
          adTitle={activeReview.adTitle}
          revieweeName={activeReview.revieweeName}
          revieweePic={activeReview.revieweePic}
          onClose={() => setActiveReview(null)}
          onSubmitted={() => {
            setPendingReviews((prev) => prev.filter((r) => r.adId !== activeReview.adId));
            setActiveReview(null);
          }}
        />
      )}
    </div>
  );
}
